import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Monitor, ExternalLink, Zap, Columns2 } from "lucide-react";
import { LANGUAGE_OPTIONS, getLanguageName } from "./constants";
import { useLanguage } from "@/i18n/LanguageContext";

export function SecondWindowCard({
  isPro,
  secondWindowEnabled,
  setSecondWindowEnabled,
  secondTargetLang,
  setSecondTargetLang,
  translationSourceLang,
  translationTargetLang,
  language,
  hasSecondScreen,
  onOpenSplitScreen
}) {
  const { t } = useLanguage();
  
  return (
    <Card className={`col-span-12 bento-card ${!isPro ? 'opacity-60' : ''}`} data-testid="second-translation-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Monitor className="w-5 h-5" />
          {t("secondTranslationWindow")}
          {isPro ? (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
              {t("optional")}
            </span>
          ) : (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              PRO
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isPro ? (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  checked={secondWindowEnabled}
                  onCheckedChange={setSecondWindowEnabled}
                  id="second-window-toggle"
                  data-testid="second-window-toggle"
                />
                <Label htmlFor="second-window-toggle" className="text-zinc-300 cursor-pointer">
                  {t("enableSecondWindow")}
                </Label>
              </div>
              {secondWindowEnabled && secondTargetLang && (
                <span className="text-sm text-green-400">
                  Active: {getLanguageName(language)} ↔ {getLanguageName(secondTargetLang)}
                </span>
              )}
            </div>
            
            {secondWindowEnabled && (
              <div className="space-y-4 pt-2 border-t border-zinc-800">
                <div>
                  <Label className="text-zinc-400 text-sm mb-2 block">
                    {t("targetLanguage")} ({getLanguageName(language)} ↔)
                  </Label>
                  <Select 
                    value={secondTargetLang} 
                    onValueChange={setSecondTargetLang}
                  >
                    <SelectTrigger className="w-full bg-zinc-900 border-zinc-700 text-white" data-testid="second-target-language-select">
                      <SelectValue placeholder={t("selectTargetLanguage")} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700" data-testid="second-target-language-options">
                      {LANGUAGE_OPTIONS.filter(lang => 
                        lang.code !== translationSourceLang && lang.code !== translationTargetLang
                      ).map(lang => (
                        <SelectItem 
                          key={lang.code} 
                          value={lang.code}
                          data-testid={`second-target-language-${lang.code}`}
                          className="text-white hover:bg-zinc-800"
                        >
                          {lang.flag} {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {secondTargetLang && (
                  <Button
                    onClick={() => {
                      const url = `${window.location.origin}/translation2`;
                      window.open(url, "translation_window_2", "width=1200,height=800");
                    }}
                    variant="outline"
                    className="w-full border-blue-600 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300"
                    data-testid="open-second-translation-window"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t("openSecondWindowFull")} ({getLanguageName(language)} ↔ {getLanguageName(secondTargetLang)})
                  </Button>
                )}

                {secondTargetLang && hasSecondScreen && (
                  <Button
                    onClick={onOpenSplitScreen}
                    variant="outline"
                    className="w-full border-green-600 hover:bg-green-600/20 text-green-400 hover:text-green-300"
                    data-testid="open-split-screen-windows"
                  >
                    <Columns2 className="w-4 h-4 mr-2" />
                    {t("splitScreenButton")}
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">
              {t("upgradeProSecondWindow")}
            </p>
            <div className="flex flex-col items-center gap-3 py-4">
              <p className="text-zinc-500 text-xs text-center">
                {t("secondWindowProOnly")}
              </p>
              <Link to="/pricing">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                  <Zap className="w-4 h-4 mr-2" />
                  {t("upgradePro")}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
